import React, { useEffect, useState } from "react";
import { AdminShell } from "./AdminShell";
import { authApi } from "../authApi";
import { Tag, Trash2, Plus, Save, Coins, Loader2, Check } from "lucide-react";

export default function AdminCredits({ embedded } = {}) {
  const [promos, setPromos] = useState([]);
  const [config, setConfig] = useState(null);
  const [commissions, setCommissions] = useState(null);
  const [form, setForm] = useState({ code: "", type: "credits", value: 2, max_uses: "", expires_at: "", once_per_member: true });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setPromos(await authApi.adminPromos()); } catch (_) {}
    try { setConfig(await authApi.adminGetCreditConfig()); } catch (_) {}
    try { setCommissions(await authApi.adminCommissions()); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const createPromo = async () => {
    setErr(""); setMsg("");
    try {
      const payload = { ...form, value: Number(form.value), max_uses: form.max_uses ? Number(form.max_uses) : null };
      await authApi.adminCreatePromo(payload);
      setMsg("Code créé"); setForm({ code: "", type: "credits", value: 2, max_uses: "", expires_at: "", once_per_member: true });
      load();
    } catch (e) { setErr(e.message || "Erreur"); }
  };

  const del = async (code) => { await authApi.adminDeletePromo(code); load(); };

  const saveConfig = async () => {
    setSaving(true); setErr(""); setMsg("");
    try { await authApi.adminUpdateCreditConfig(config); setMsg("Configuration enregistrée"); }
    catch (e) { setErr(e.message || "Erreur"); }
    setSaving(false);
  };

  const setCfg = (path, val) => {
    setConfig((c) => {
      const n = JSON.parse(JSON.stringify(c));
      const keys = path.split("."); let o = n;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = val; return n;
    });
  };

  return (
    <AdminShell embedded={embedded} title="Crédits & Promotions">
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Codes promo */}
        <section className="border border-white/10 p-6">
          <h2 className="text-lg font-light mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-[#C19A6B]" /> Codes promo</h2>
          <div className="space-y-3 mb-6">
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="promo-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="CODE" className="bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
              <select data-testid="promo-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="bg-[#0d0d10] border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]">
                <option value="credits">Crédits offerts</option>
                <option value="percent">Remise %</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-zinc-400">{form.type === "credits" ? "Crédits" : "% de remise"}
                <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
              </label>
              <label className="text-xs text-zinc-400">Utilisations max (vide=∞)
                <input type="number" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="mt-1 w-full bg-transparent border border-white/15 px-3 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
              </label>
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300"><input type="checkbox" checked={form.once_per_member} onChange={(e) => setForm({ ...form, once_per_member: e.target.checked })} className="accent-[#C19A6B]" /> Une seule fois par membre</label>
            <button onClick={createPromo} data-testid="create-promo-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-medium uppercase tracking-wider px-4 py-2 transition-colors"><Plus className="w-4 h-4" /> Créer le code</button>
          </div>
          <div className="space-y-2">
            {promos.length === 0 && <p className="text-sm text-zinc-500">Aucun code pour l'instant.</p>}
            {promos.map((p) => (
              <div key={p.code} data-testid={`promo-row-${p.code}`} className="flex items-center justify-between border border-white/10 px-3 py-2">
                <div>
                  <span className="text-white font-medium">{p.code}</span>
                  <span className="text-xs text-[#C19A6B] ml-2">{p.type === "credits" ? `+${p.value} crédits` : `−${p.value}%`}</span>
                  <span className="text-[11px] text-zinc-500 ml-2">{p.uses}{p.max_uses ? `/${p.max_uses}` : ""} util.</span>
                </div>
                <button onClick={() => del(p.code)} data-testid={`del-promo-${p.code}`} className="text-zinc-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>

        {/* Configuration barème */}
        <section className="border border-white/10 p-6">
          <h2 className="text-lg font-light mb-4 flex items-center gap-2"><Coins className="w-4 h-4 text-[#C19A6B]" /> Barème & crédits</h2>
          {config && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <label className="text-xs text-zinc-400">Prix / crédit (€)
                  <input type="number" data-testid="cfg-price" value={config.price_per_credit_eur ?? 0} onChange={(e) => setCfg("price_per_credit_eur", Number(e.target.value))} className="mt-1 w-full bg-transparent border border-white/15 px-2 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
                </label>
                <label className="text-xs text-zinc-400">Bonus parrainage
                  <input type="number" data-testid="cfg-referral" value={config.referral_bonus ?? 0} onChange={(e) => setCfg("referral_bonus", Number(e.target.value))} className="mt-1 w-full bg-transparent border border-white/15 px-2 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
                </label>
                <label className="text-xs text-zinc-400">Annonces gratuites
                  <input type="number" data-testid="cfg-listing-limit" value={config.free_listing_limit ?? 0} onChange={(e) => setCfg("free_listing_limit", Number(e.target.value))} className="mt-1 w-full bg-transparent border border-white/15 px-2 py-2 text-sm text-white outline-none focus:border-[#C19A6B]" />
                </label>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider text-zinc-500">Coût par niveau (crédits)</p>
                {["standard", "premium", "prestige"].map((k) => config.tiers?.[k] && (
                  <div key={k} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-300 w-24 capitalize">{config.tiers[k].label}</span>
                    <input type="number" step="0.5" data-testid={`cfg-tier-${k}`} value={config.tiers[k].cost ?? 0} onChange={(e) => setCfg(`tiers.${k}.cost`, Number(e.target.value))} className="w-24 bg-transparent border border-white/15 px-2 py-1.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
                    <span className="text-xs text-zinc-500">staging max</span>
                    <input type="number" data-testid={`cfg-staging-${k}`} value={config.tiers[k].max_staging ?? 0} onChange={(e) => setCfg(`tiers.${k}.max_staging`, Number(e.target.value))} className="w-20 bg-transparent border border-white/15 px-2 py-1.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-wider text-zinc-500">Commissions parrainage (% par niveau, versées en crédits)</p>
                <div className="flex flex-wrap items-center gap-2">
                  {(config.commission_rates || []).map((r, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-xs text-zinc-400">N{i + 1}</span>
                      <input type="number" data-testid={`cfg-commission-${i}`} value={r ?? 0} onChange={(e) => { const arr = [...config.commission_rates]; arr[i] = Number(e.target.value); setCfg("commission_rates", arr); }} className="w-16 bg-transparent border border-white/15 px-2 py-1.5 text-sm text-white outline-none focus:border-[#C19A6B]" />
                      <span className="text-xs text-zinc-500">%</span>
                    </div>
                  ))}
                  <button onClick={() => setCfg("commission_rates", [...(config.commission_rates || []), 1])} data-testid="add-commission-level" className="text-xs border border-white/15 px-2 py-1.5 hover:border-[#C19A6B] text-zinc-300">+ niveau</button>
                  {(config.commission_rates || []).length > 0 && <button onClick={() => setCfg("commission_rates", config.commission_rates.slice(0, -1))} className="text-xs border border-white/15 px-2 py-1.5 hover:border-red-400 text-zinc-300">− niveau</button>}
                </div>
              </div>
              <button onClick={saveConfig} disabled={saving} data-testid="save-config-btn" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white text-sm font-medium uppercase tracking-wider px-4 py-2 transition-colors">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
            </div>
          )}
        </section>
      </div>

      {/* Commissions distribuées (vue globale) */}
      <section className="border border-white/10 p-6 mt-8" data-testid="admin-commissions">
        <h2 className="text-lg font-light mb-4 flex items-center gap-2"><Coins className="w-4 h-4 text-[#C19A6B]" /> Commissions parrainage distribuées</h2>
        {commissions ? (
          <>
            <div className="flex flex-wrap gap-8 mb-5">
              <div><p className="text-xs uppercase tracking-wider text-zinc-500">Total distribué</p><p className="text-2xl font-light text-[#C19A6B]">{commissions.total_distributed} crédits</p></div>
              <div><p className="text-xs uppercase tracking-wider text-zinc-500">Commissions</p><p className="text-2xl font-light text-white">{commissions.count}</p></div>
            </div>
            <div className="space-y-2">
              {(commissions.beneficiaries || []).length === 0 && <p className="text-sm text-zinc-500">Aucune commission distribuée pour l'instant.</p>}
              {(commissions.beneficiaries || []).map((b) => (
                <div key={b.member_id} className="flex items-center justify-between border-b border-white/5 pb-2 text-sm">
                  <span className="text-zinc-300">{b.name || b.email || b.member_id}</span>
                  <span className="text-[#C19A6B]">{b.total_credits} crédits</span>
                </div>
              ))}
            </div>
          </>
        ) : <p className="text-sm text-zinc-500">Chargement…</p>}
      </section>

      {(msg || err) && <p className={`mt-4 text-sm ${err ? "text-red-400" : "text-green-400"}`} data-testid="admin-credits-msg">{err || msg}</p>}
    </AdminShell>
  );
}
